import { useState, lazy, Suspense } from "react";
import { Link } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import {
  Code2, Smartphone, Boxes, Building2, Sparkles, Globe, Cloud,
  Megaphone, ArrowRight, Check, ChevronRight, Loader2,
} from "lucide-react";

const DomainSearch = lazy(() =>
  import("./domain-search").then((m) => ({ default: m.DomainSearch }))
);

type ServiceKey =
  | "web" | "mobile" | "saas" | "erp" | "ai" | "domains" | "hosting" | "marketing";

type Service = {
  key: ServiceKey;
  label: string;
  tagline: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
};

const SERVICES: Service[] = [
  { key: "domains",   label: "Domain Search",   tagline: "Live availability check", icon: Globe,      accent: "from-teal-500 to-cyan-500" },
  { key: "hosting",   label: "Hosting Plans",   tagline: "Shared, VPS, Cloud",       icon: Cloud,      accent: "from-sky-500 to-indigo-500" },
  { key: "web",       label: "Web Development", tagline: "Modern websites & apps",   icon: Code2,      accent: "from-indigo-500 to-blue-500" },
  { key: "mobile",    label: "Mobile Apps",     tagline: "iOS, Android, cross-platform", icon: Smartphone, accent: "from-pink-500 to-rose-500" },
  { key: "saas",      label: "SaaS Platforms",  tagline: "Multi-tenant products",    icon: Boxes,      accent: "from-violet-500 to-purple-500" },
  { key: "erp",       label: "ERP & POS",       tagline: "Business operations",      icon: Building2,  accent: "from-emerald-500 to-teal-500" },
  { key: "ai",        label: "AI Automation",   tagline: "Agents & workflows",       icon: Sparkles,   accent: "from-fuchsia-500 to-purple-500" },
  { key: "marketing", label: "Digital Marketing", tagline: "SEO, Ads, Social",       icon: Megaphone,  accent: "from-amber-500 to-orange-500" },
];

export function ServicesExplorer() {
  const [active, setActive] = useState<ServiceKey>("domains");
  const current = SERVICES.find((s) => s.key === active)!;

  return (
    <section className="relative py-24">
      <div className="absolute inset-0 -z-10 bg-hero-glow opacity-50" />
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs font-semibold uppercase tracking-widest text-primary">What we do</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">
            Explore our <span className="text-gradient">services</span>
          </h2>
          <p className="mt-3 text-muted-foreground">
            Pick a service on the left — details, plans and live tools appear on the right.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-12">
          {/* Left list */}
          <div className="lg:col-span-4">
            <ul className="rounded-2xl border border-border bg-card p-2 shadow-card">
              {SERVICES.map((s) => {
                const isActive = s.key === active;
                return (
                  <li key={s.key}>
                    <button
                      onClick={() => setActive(s.key)}
                      className={`relative w-full text-left flex items-center gap-3 rounded-xl px-3 py-3 transition ${
                        isActive ? "bg-secondary" : "hover:bg-secondary/60"
                      }`}
                    >
                      {isActive && (
                        <motion.span
                          layoutId="services-explorer-active"
                          className="absolute inset-0 -z-10 rounded-xl border border-primary/30 bg-gradient-brand/5"
                          transition={{ type: "spring", stiffness: 380, damping: 32 }}
                        />
                      )}
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${s.accent} text-white shadow-card`}
                      >
                        <s.icon className="h-5 w-5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-foreground truncate">{s.label}</span>
                        <span className="block text-xs text-muted-foreground truncate">{s.tagline}</span>
                      </span>
                      <ChevronRight
                        className={`h-4 w-4 shrink-0 transition ${
                          isActive ? "text-primary translate-x-0.5" : "text-muted-foreground"
                        }`}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Right panel */}
          <div className="lg:col-span-8">
            <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-card">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                >
                  <PanelHeader service={current} />
                  <div className="p-5 sm:p-6">{renderPanel(active)}</div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PanelHeader({ service }: { service: Service }) {
  return (
    <div className="relative overflow-hidden border-b border-border p-5 sm:p-6">
      <div className={`absolute inset-0 -z-10 bg-gradient-to-br ${service.accent} opacity-[0.12]`} />
      <div className="flex items-center gap-3">
        <span className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${service.accent} text-white shadow-glow`}>
          <service.icon className="h-6 w-6" />
        </span>
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-primary">{service.tagline}</div>
          <h3 className="text-xl font-bold tracking-tight">{service.label}</h3>
        </div>
      </div>
    </div>
  );
}

function renderPanel(key: ServiceKey) {
  switch (key) {
    case "domains":
      return (
        <Suspense fallback={<PanelLoading />}>
          <DomainSearch embedded />
        </Suspense>
      );
    case "hosting":
      return <HostingPanel />;
    default:
      return <ServicePanel data={SERVICE_DETAILS[key]} />;
  }
}

function PanelLoading() {
  return (
    <div className="flex items-center justify-center py-16 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  );
}

const SERVICE_DETAILS: Record<Exclude<ServiceKey, "domains" | "hosting">, {
  description: string;
  features: string[];
  cta: { label: string; to: string };
  stat: { value: string; label: string };
}> = {
  web: {
    description: "Pixel-perfect, blazing-fast websites and web apps built on modern stacks (React, Next.js, TanStack).",
    features: ["Responsive design", "SEO-ready", "Lighthouse 95+", "Headless CMS"],
    cta: { label: "Start a web project", to: "/contact" },
    stat: { value: "120+", label: "websites shipped" },
  },
  mobile: {
    description: "Native iOS and Android apps plus cross-platform builds with React Native and Flutter.",
    features: ["App Store launch", "Push & analytics", "Offline-first", "CI/CD pipelines"],
    cta: { label: "Build my app", to: "/contact" },
    stat: { value: "40+", label: "apps in production" },
  },
  saas: {
    description: "Multi-tenant SaaS platforms with auth, billing, dashboards, and granular role permissions.",
    features: ["Stripe billing", "Multi-tenant DB", "Admin console", "Audit logs"],
    cta: { label: "Plan my SaaS", to: "/contact" },
    stat: { value: "$2M+", label: "ARR powered" },
  },
  erp: {
    description: "Tailored ERP, POS, HRM and accounting systems for retail, F&B, healthcare and services.",
    features: ["POS terminals", "Inventory sync", "Payroll & HR", "Tax & VAT"],
    cta: { label: "Explore ERP suite", to: "/software" },
    stat: { value: "60+", label: "businesses automated" },
  },
  ai: {
    description: "AI agents, workflow automation and integrations powered by LLMs to slash manual work.",
    features: ["Custom agents", "RAG over your data", "Workflow automation", "Voice & chat"],
    cta: { label: "Automate with AI", to: "/services" },
    stat: { value: "10×", label: "average productivity" },
  },
  marketing: {
    description: "Performance marketing across SEO, Google, Meta and content — built around revenue, not vanity.",
    features: ["SEO & content", "Google & Meta Ads", "Conversion tracking", "Monthly reports"],
    cta: { label: "Grow with us", to: "/services" },
    stat: { value: "4.6×", label: "average ROAS" },
  },
};

function ServicePanel({ data }: { data: typeof SERVICE_DETAILS[keyof typeof SERVICE_DETAILS] }) {
  return (
    <div className="grid gap-6 sm:grid-cols-5">
      <div className="sm:col-span-3 space-y-4">
        <p className="text-sm leading-relaxed text-muted-foreground">{data.description}</p>
        <ul className="grid grid-cols-2 gap-2">
          {data.features.map((f) => (
            <li key={f} className="flex items-center gap-2 rounded-md border border-border bg-background/50 px-3 py-2 text-xs font-medium">
              <Check className="h-3.5 w-3.5 text-primary" />
              {f}
            </li>
          ))}
        </ul>
        <Link
          to={data.cta.to}
          className="inline-flex items-center gap-1.5 rounded-md bg-gradient-brand px-4 py-2.5 text-sm font-semibold text-white shadow-glow hover:opacity-95 transition"
        >
          {data.cta.label} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="sm:col-span-2">
        <div className="relative h-full overflow-hidden rounded-xl border border-border bg-gradient-brand/10 p-5">
          <Sparkles className="absolute right-3 top-3 h-5 w-5 text-primary/70" />
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">By the numbers</div>
          <div className="mt-2 text-4xl font-bold text-gradient">{data.stat.value}</div>
          <div className="mt-1 text-sm text-muted-foreground">{data.stat.label}</div>
        </div>
      </div>
    </div>
  );
}

function HostingPanel() {
  const plans = [
    { name: "Starter",   price: 3,  features: ["10 GB SSD", "1 site", "Free SSL", "Email"], badge: null },
    { name: "Business",  price: 9,  features: ["100 GB SSD", "Unlimited sites", "Daily backups", "Free CDN"], badge: "Popular" },
    { name: "VPS Cloud", price: 24, features: ["4 vCPU · 8 GB RAM", "Root access", "NVMe storage", "DDoS protection"], badge: null },
  ];
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {plans.map((p) => (
        <div
          key={p.name}
          className={`relative rounded-xl border p-4 transition ${
            p.badge ? "border-primary/40 bg-gradient-brand/5" : "border-border bg-background/50"
          }`}
        >
          {p.badge && (
            <span className="absolute -top-2 right-3 rounded-full bg-gradient-brand px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
              {p.badge}
            </span>
          )}
          <div className="text-sm font-semibold">{p.name}</div>
          <div className="mt-1">
            <span className="text-2xl font-bold text-gradient">${p.price}</span>
            <span className="text-xs text-muted-foreground">/mo</span>
          </div>
          <ul className="mt-3 space-y-1.5">
            {p.features.map((f) => (
              <li key={f} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Check className="h-3 w-3 text-primary" /> {f}
              </li>
            ))}
          </ul>
          <Link
            to="/hosting"
            className="mt-4 inline-flex w-full items-center justify-center gap-1 rounded-md bg-gradient-brand px-3 py-2 text-xs font-semibold text-white shadow-glow hover:opacity-95 transition"
          >
            Choose plan
          </Link>
        </div>
      ))}
    </div>
  );
}
